// SUB-OFFICE 7 — first-person hands: the FP presence anchor. Two paper-grey
// box-mitt hands in the figure language (forearm, palm, finger masses) parented
// to the camera, raised only when the petitioner holds something the Bureau
// issued: the printed Form 7A (tractor-feed texture), the rolled canister, or
// the verdict slip during VERDICT/ACK. Empty hands stay out of frame — the
// crosshair is enough. Sway rides the head-bob (zero under reduced motion);
// bodies ease (220ms raise), and the E-press lands as a one-beat dip.

import {
  BoxGeometry, Color, DoubleSide, Group, Mesh, MeshBasicMaterial,
  MeshLambertMaterial, PlaneGeometry,
} from 'three';
import { PAL, shade } from '../palette';
import { makePaperFormTexture } from './textures';
import { patchMaterial } from './scene';
import type { FirstPersonView } from './fpview';
import type { Entity, EventBus } from '../entities/entities';
import type { Player } from '../entities/player';
import type { Fsm } from '../state';

type HandState = 'EMPTY' | 'PAPER' | 'CANISTER' | 'SLIP';

const RAISE_RATE = 1 / 0.22; // 220ms raise/lower — bodies ease
const DOWN_Y = -0.66;
const UP_Y = -0.42;

export class FpHands implements Entity {
  readonly id = 'fphands';
  readonly group = new Group();

  private paper: Mesh;
  private slip: Mesh;
  private canister: Mesh;
  private handL: Group;
  private handR: Group;
  private raise = 0; // 0 lowered .. 1 held up
  private press = 0; // E-press dip envelope
  private state: HandState = 'EMPTY';

  constructor(
    bus: EventBus,
    private player: Player,
    private fsm: Fsm,
    private fpv: FirstPersonView,
    private isFp: () => boolean,
    private reduced: boolean
  ) {
    const skin = new MeshLambertMaterial({
      color: new Color(shade(PAL.ghost, 0.3)).convertSRGBToLinear(), flatShading: true,
    });
    patchMaterial(skin);

    const buildHand = (side: 1 | -1): Group => {
      const h = new Group();
      const forearm = new Mesh(new BoxGeometry(0.06, 0.06, 0.24), skin);
      forearm.position.set(0, -0.03, 0.14);
      forearm.rotation.x = 0.5;
      h.add(forearm);
      const palm = new Mesh(new BoxGeometry(0.085, 0.035, 0.11), skin);
      h.add(palm);
      const fingers = new Mesh(new BoxGeometry(0.08, 0.028, 0.05), skin);
      fingers.position.set(0, 0.012, -0.075);
      fingers.rotation.x = -0.35;
      h.add(fingers);
      const thumb = new Mesh(new BoxGeometry(0.025, 0.025, 0.055), skin);
      thumb.position.set(side * -0.05, 0.012, -0.02);
      thumb.rotation.y = side * 0.5;
      h.add(thumb);
      return h;
    };
    this.handR = buildHand(1);
    this.handR.position.set(0.17, -0.03, -0.02);
    this.handL = buildHand(-1);
    this.handL.position.set(-0.09, -0.04, -0.03);
    this.group.add(this.handR, this.handL);

    // the held form: the same tractor-feed print the windows issue
    const paperMat = new MeshBasicMaterial({
      map: makePaperFormTexture(), transparent: true, side: DoubleSide,
    });
    patchMaterial(paperMat);
    this.paper = new Mesh(new PlaneGeometry(0.26, 0.35), paperMat);
    this.paper.position.set(0.05, 0.05, -0.06);
    this.paper.rotation.x = -0.5; // tilted toward the eye, legible
    this.paper.rotation.z = -0.05; // held, not mounted
    this.group.add(this.paper);

    // the verdict slip: narrower stock, same print voice
    this.slip = new Mesh(new PlaneGeometry(0.2, 0.34), paperMat);
    this.slip.position.set(0, 0.08, -0.05);
    this.slip.rotation.x = -0.42;
    this.group.add(this.slip);

    // the rolled canister: brass-amber tube riding the right hand
    const canMat = new MeshBasicMaterial({ color: new Color(PAL.amber).convertSRGBToLinear() });
    patchMaterial(canMat);
    this.canister = new Mesh(new BoxGeometry(0.055, 0.055, 0.3), canMat);
    this.canister.position.set(0.16, 0.02, -0.03);
    this.canister.rotation.z = 0.2;
    this.group.add(this.canister);

    // camera-space anchor; boot parents this.group to the camera
    this.group.position.set(0, DOWN_Y, -0.52);
    this.group.visible = false;

    // the E-press lands in the body: a one-beat dip of whatever is held
    bus.on('interact', () => {
      if (this.isFp() && this.raise > 0.5) this.press = 1;
    });
  }

  /** What the hands should be doing this frame, from authoritative state. */
  private desired(): HandState {
    const s = this.fsm.state;
    if (s === 'VERDICT' || s === 'ACK') return 'SLIP';
    const c = this.player.carrying;
    if (c === 'paper') return 'PAPER';
    if (c === 'canister') return 'CANISTER';
    return 'EMPTY';
  }

  update(dt: number): void {
    const want = this.isFp() ? this.desired() : 'EMPTY';
    if (want !== 'EMPTY') this.state = want; // keep the held item during lowering
    const target = want === 'EMPTY' ? 0 : 1;
    this.raise += Math.sign(target - this.raise) * Math.min(dt * RAISE_RATE, Math.abs(target - this.raise));
    this.press = Math.max(0, this.press - dt * 6);

    const up = this.raise;
    this.group.visible = up > 0.02;
    if (!this.group.visible) return;

    this.paper.visible = this.state === 'PAPER';
    this.slip.visible = this.state === 'SLIP';
    this.canister.visible = this.state === 'CANISTER';
    this.handL.visible = this.state !== 'CANISTER'; // one-handed carry for the tube

    // raise curve + head-bob sway (bob amp is zero under reduced motion)
    const phase = this.fpv.bobPhaseValue;
    const amp = this.reduced ? 0 : this.fpv.bobAmpValue;
    const swayY = -Math.abs(Math.sin(phase)) * 0.016 * amp;
    const swayX = Math.sin(phase * 0.5) * 0.008 * amp;
    const dip = this.press * 0.035;
    this.group.position.set(swayX, DOWN_Y + (UP_Y - DOWN_Y) * up + swayY - dip, -0.52);
    this.group.rotation.z = Math.sin(phase) * 0.012 * amp;
    this.group.rotation.x = (1 - up) * -0.5 + this.press * 0.12;
  }
}
